// src/services/api.js

const API_ROOT = process.env.REACT_APP_API_ROOT || "";

// --------- Token & Org helpers ----------
function getToken() {
  return localStorage.getItem("token");
}
export function getSelectedOrgId() {
  const v = localStorage.getItem("selectedOrgId");
  if (v === null || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}
export function setSelectedOrgId(orgId) {
  if (orgId === null || orgId === undefined || orgId === "") {
    localStorage.removeItem("selectedOrgId");
  } else {
    localStorage.setItem("selectedOrgId", String(orgId));
  }
}
export function clearAuthStorage() {
  localStorage.removeItem("token");
  localStorage.removeItem("selectedOrgId");
}

// --------- Endpoint normalizer ----------
function normalize(endpoint) {
  if (!endpoint || typeof endpoint !== "string") return endpoint;
  if (endpoint.startsWith("/api/")) return endpoint;

  const needsApi = [
    "/auth/",
    "/users",
    "/candidates",
    "/interviews",
    "/job_requisitions",
    "/jobs",
    "/analytics",
    "/candidate_interview_report",
    "/organizations",
    "/billing",
  ];
  if (needsApi.some((prefix) => endpoint.startsWith(prefix))) {
    return "/api" + endpoint;
  }
  return endpoint;
}

export function getJSON(params) {
  if (!params) return "";
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") usp.set(k, v);
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
}

// --------- Core fetch wrapper ----------
export async function apiFetch(endpoint, options = {}) {
  const normalized = normalize(endpoint);
  const token = getToken();
  const selectedOrgId = getSelectedOrgId();

  // Never send Authorization on login
  const isLogin = normalized === "/api/auth/login";

  // IMPORTANT: put options.headers LAST so per-request overrides (like x-org-id) win
  const baseHeaders = {
    "Content-Type": "application/json",
    ...(token && !isLogin ? { Authorization: `Bearer ${token}` } : {}),
    ...(selectedOrgId !== null ? { "x-org-id": String(selectedOrgId) } : {}),
  };
  const headers = { ...baseHeaders, ...(options.headers || {}) };

  const opts = { ...options, headers, credentials: options.credentials ?? "include" };
  const url = API_ROOT + normalized;

  let response, text, data;
  try {
    response = await fetch(url, opts);
    text = await response.text();
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = { error: "Non-JSON response", detail: text };
    }
    if (!response.ok) {
      const msg = data?.error || data?.message || response.statusText;
      throw new Error(msg);
    }
    return data;
  } catch (err) {
    const msg = typeof err === "string" ? err : err?.message || "API request failed";
    return Promise.reject(msg);
  }
}

/**
 * Explicitly call an endpoint for a specific organization id, regardless of global selection.
 * Used to aggregate data across orgs when superadmin selects “All”.
 */
export function apiFetchForOrg(endpoint, orgId, options = {}) {
  const headers = { ...(options.headers || {}), "x-org-id": String(orgId) };
  return apiFetch(endpoint, { ...options, headers });
}

// --------- High-level helpers ----------

// Profile: use the self endpoint your backend exposes via app.js
export function getProfile() {
  return apiFetch("/api/me");
}

// Login accepts username + password; returns { token, user }
export function login(username, password) {
  return apiFetch("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

export function register({ username, password, role, organization_id }) {
  const body = organization_id
    ? { username, password, role, organization_id }
    : { username, password, role };
  return apiFetch("/api/auth/register", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function fetchOrganizations() {
  return apiFetch("/api/organizations");
}

// Simple verbs for pages
export function post(endpoint, payload, config = {}) {
  return apiFetch(normalize(endpoint), {
    method: "POST",
    body: JSON.stringify(payload),
    ...config,
    headers: { "Content-Type": "application/json", ...(config.headers || {}) },
  });
}
export function put(endpoint, payload, config = {}) {
  return apiFetch(normalize(endpoint), {
    method: "PUT",
    body: JSON.stringify(payload),
    ...config,
    headers: { "Content-Type": "application/json", ...(config.headers || {}) },
  });
}
export function del(endpoint, config = {}) {
  return apiFetch(normalize(endpoint), {
    method: "DELETE",
    ...config,
    headers: { "Content-Type": "application/json", ...(config.headers || {}) },
  });
}
export { del as delete };

// Axios-like aliases some pages use
export const api = {
  get: (url) => apiFetch(`/api${url}`),
  post: (url, body, config = {}) =>
    apiFetch(`/api${url}`, { method: "POST", body: JSON.stringify(body), headers: config.headers }),
  put: (url, body, config = {}) =>
    apiFetch(`/api${url}`, { method: "PUT", body: JSON.stringify(body), headers: config.headers }),
  delete: (url, config = {}) =>
    apiFetch(`/api${url}`, { method: "DELETE", headers: config.headers }),
};

export default api;

// Billing/subscription helpers
export async function fetchBillingTransactions(params = {}) {
  return apiFetch(`/api/billing/transactions${getJSON(params)}`);
}
export async function fetchSubscriptionTransactions(params = {}) {
  return apiFetch(`/api/billing/subscriptions${getJSON(params)}`);
}
export async function fetchBillingSummary(params = {}) {
  return apiFetch(`/api/billing/summary${getJSON(params)}`);
}
export async function fetchJobSubmissions(jobReqId) {
  return apiFetch(`/api/jobs/${jobReqId}/submissions`);
}
export async function upsertJobSubmission(jobReqId, body) {
  return post(`/api/jobs/${jobReqId}/submissions`, body);
}
export async function fetchCandidateSubmissions(candidateId) {
  return apiFetch(`/api/candidates/${candidateId}/submissions`);
}

// Convenience helpers used by pages
export async function fetchAnalyticsSummary() {
  return apiFetch("/api/analytics/summary");
}
export async function fetchJobs(params) {
  return apiFetch(`/api/jobs${getJSON(params)}`);
}
export async function fetchInterviews() {
  return apiFetch("/api/interviews");
}
export async function fetchCandidates() {
  return apiFetch("/api/candidates");
}
